import { useUserContext } from "@Hooks/contexts/useUserContext";
import { styled } from "@mui/material/styles";
import { Banner, Button, Card, ClockIcon, Meta, ProgressBar, Spinner } from "@UI";
import React from "react";
import { useTranslation } from "react-i18next";
import { fetchUsageMeterStatus, postUsageMeterUpgrade } from "../api/chatApi";
import { textOf, useChat } from "../hooks/useChat";
import { useChatComposerStore } from "../store/chatComposerStore";
import { BannerAction, HookTile } from "./cards/HookCard";
import { QuestionDock } from "./cards/QuestionDock";
import { Input } from "./Input";
import { MessageList } from "./MessageList";
import { Welcome } from "./Welcome";
const Screen = styled("div")({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0
});
const Blank = styled("div")({
    flex: 1,
    display: "grid",
    placeItems: "center"
});
const Notices = styled("div")({
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-1) var(--j-sp-3) 0"
});
const LimitCard = styled(Card)({
    padding: "var(--j-sp-3)",
    border: "1px solid var(--j-line)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    "& .limit-head": {
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--j-sp-2)"
    },
    "& .limit-title": {
        fontSize: "var(--j-fs-md)",
        lineHeight: "var(--j-lh-md)",
        fontWeight: 600
    },
    "& .limit-desc": {
        margin: "var(--j-sp-0-5) 0 0",
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)"
    },
    "& .limit-bar > *": {
        background: "var(--j-warn)"
    },
    "& .limit-foot": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--j-sp-2)"
    }
});
function nextResetDate(language) {
    const timeZone = "Europe/Copenhagen";
    const [year, month] = new Date().toLocaleDateString("en-CA", {
        timeZone
    }).split("-").map(Number);
    return new Date(Date.UTC(year, month, 1, 12)).toLocaleDateString(language, {
        day: "numeric",
        month: "long",
        timeZone
    });
}
export function ChatScreen({ input, setInput }) {
    const { messages, isAgentResponding, isComposerBusy, sendMessage, stop, respondToToolConfirmation, toolDecisions, respondingToolIds, choiceAnswers, answerChoice, dockedChoice, chatError, retry, isLoadingHistory } = useChat();
    const { t, i18n } = useTranslation();
    const token = useUserContext((s)=>s.user?.meta?.access_token?.token);
    const inputRef = React.useRef(null);
    const [usage, setUsage] = React.useState(null);
    const [isLimitShown, setIsLimitShown] = React.useState(false);
    const [isContinuing, setIsContinuing] = React.useState(false);
    const [hasContinueFailed, setHasContinueFailed] = React.useState(false);
    const [heldText, setHeldText] = React.useState(null);
    const attachments = useChatComposerStore((s)=>s.attachments);
    const uploadingCount = attachments.filter((attachment)=>attachment.status === "uploading").length;
    const hasFailedUpload = attachments.some((attachment)=>attachment.status === "failed");
    const canContinue = usage?.canUpgrade !== false;
    const isAwaitingCard = isAgentResponding && !isComposerBusy;
    React.useEffect(()=>{
        if (token) fetchUsageMeterStatus(token).then(setUsage, ()=>undefined);
    }, [
        token
    ]);
    const restoreRefused = (refused)=>{
        if (!refused) return;
        if (refused.text) setInput(refused.text);
        setUsage({
            blocked: true,
            canUpgrade: refused.canUpgrade
        });
        setHasContinueFailed(false);
        setIsLimitShown(true);
    };
    const dispatch = (text)=>{
        const { attachments: sent, connections, setAttachments } = useChatComposerStore.getState();
        const documents = sent.flatMap((attachment)=>attachment.upload ? [
                {
                    persisted: false,
                    link: attachment.upload.link,
                    filename: attachment.upload.filename,
                    extension: attachment.upload.subtype
                }
            ] : []);
        const metadata = {
            ...documents.length > 0 && {
                documents
            },
            ...connections.length > 0 && {
                connections: connections.map(({ id, type, title })=>({
                        id,
                        type,
                        title: title ?? ""
                    }))
            }
        };
        setAttachments([]);
        sendMessage({
            text,
            metadata
        }).then((refused)=>{
            if (refused) setAttachments(sent);
            restoreRefused(refused);
        });
    };
    React.useEffect(()=>{
        if (heldText === null || uploadingCount > 0) return;
        setHeldText(null);
        if (hasFailedUpload) return;
        setInput("");
        dispatch(heldText);
    }, [
        heldText,
        uploadingCount
    ]);
    const send = (text)=>{
        if (usage?.blocked) {
            setIsLimitShown(true);
            fetchUsageMeterStatus(token).then((status)=>{
                setUsage(status);
                if (!status.blocked) setIsLimitShown(false);
            }, ()=>undefined);
            return false;
        }
        if (hasFailedUpload) return false;
        if (uploadingCount > 0) {
            setHeldText(text);
            return false;
        }
        dispatch(text);
        return true;
    };
    const resendLastPrompt = ()=>{
        const prompt = [
            ...messages
        ].reverse().find((message)=>message.role === "user");
        if (prompt) send(textOf(prompt));
    };
    const continueWork = async ()=>{
        setIsContinuing(true);
        const status = await postUsageMeterUpgrade(token).catch(()=>fetchUsageMeterStatus(token)).catch(()=>null);
        setIsContinuing(false);
        if (status) setUsage(status);
        setHasContinueFailed(!status || status.blocked);
        if (status && !status.blocked) setIsLimitShown(false);
    };
    const pickSuggestion = (prompt)=>{
        setInput(prompt);
        requestAnimationFrame(()=>{
            const field = inputRef.current;
            field?.focus();
            field?.setSelectionRange(prompt.length, prompt.length);
        });
    };
    const notice = heldText !== null ? {
        text: t("chat.attachments.waiting", {
            count: uploadingCount
        })
    } : hasFailedUpload ? {
        text: t("chat.attachments.failedHint"),
        isError: true
    } : null;
    const meta = isComposerBusy ? t("chat.composer.working") : isAwaitingCard ? t("chat.composer.waiting") : isLimitShown ? t("chat.composer.kept") : messages.length === 0 ? t("chat.composer.hint") : null;
    return /*#__PURE__*/ React.createElement(Screen, null, messages.length === 0 ? isLoadingHistory ? /*#__PURE__*/ React.createElement(Blank, {
        "aria-busy": true
    }, /*#__PURE__*/ React.createElement(Spinner, {
        size: "lg"
    })) : /*#__PURE__*/ React.createElement(Welcome, {
        onPick: pickSuggestion
    }) : /*#__PURE__*/ React.createElement(MessageList, {
        messages: messages,
        isResponding: isAgentResponding,
        isBusy: isComposerBusy,
        onToolConfirmationResponse: respondToToolConfirmation,
        onRetry: resendLastPrompt,
        toolDecisions: toolDecisions,
        respondingToolIds: respondingToolIds,
        choiceAnswers: choiceAnswers
    }), (isLimitShown || chatError) && /*#__PURE__*/ React.createElement(Notices, null, chatError && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error",
        action: chatError.retryable && /*#__PURE__*/ React.createElement(BannerAction, {
            variant: "outline",
            size: "sm",
            onClick: ()=>retry().then(restoreRefused)
        }, t("chat.retry"))
    }, t(chatError.message)), isLimitShown && /*#__PURE__*/ React.createElement(LimitCard, {
        role: "alert",
        "aria-busy": isContinuing
    }, /*#__PURE__*/ React.createElement("div", {
        className: "limit-head"
    }, /*#__PURE__*/ React.createElement(HookTile, {
        "data-tone": "warn",
        "data-size": "lg"
    }, /*#__PURE__*/ React.createElement(ClockIcon, {
        size: 16
    })), /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
        className: "limit-title"
    }, t("chat.usage_limit.title")), /*#__PURE__*/ React.createElement("p", {
        className: "limit-desc"
    }, !canContinue ? t("chat.usage_limit.contact_support", {
        date: nextResetDate(i18n.language)
    }) : hasContinueFailed ? t("chat.usage_limit.continue_failed") : t("chat.usage_limit.description")))), /*#__PURE__*/ React.createElement(ProgressBar, {
        className: "limit-bar",
        value: 100,
        label: t("chat.usage_limit.allowance")
    }), /*#__PURE__*/ React.createElement("div", {
        className: "limit-foot"
    }, /*#__PURE__*/ React.createElement(Meta, null, t("chat.usage_limit.allowance")), canContinue && /*#__PURE__*/ React.createElement(Button, {
        loading: isContinuing,
        "aria-label": isContinuing ? t("chat.usage_limit.continuing") : undefined,
        onClick: continueWork
    }, t("chat.usage_limit.continue"))))), /*#__PURE__*/ React.createElement(Input, {
        value: input,
        onChange: setInput,
        onSend: send,
        onStop: heldText !== null ? ()=>setHeldText(null) : stop,
        isResponding: isComposerBusy || heldText !== null,
        disabled: heldText !== null,
        placeholder: isAwaitingCard ? t("chat.composer.replyPlaceholder") : undefined,
        meta: meta,
        notice: notice,
        inputRef: inputRef,
        dock: dockedChoice && /*#__PURE__*/ React.createElement(QuestionDock, {
            key: dockedChoice.toolCallId,
            request: dockedChoice,
            isSending: respondingToolIds.has(dockedChoice.toolCallId),
            onAnswer: answerChoice
        })
    }));
}
